import React from 'react'
import { ScrollView, View } from 'react-native'
import { Circle, ContentLoader, Rect } from '@src/components/ContentLoader'
import { StyleService, useStyleSheet } from '@src/style/service'

const LABEL_HEIGHT = 12
const ROW_HEIGHT = 96
const ROW_HEIGHT_WITH_ACTION = 146
const DIVIDER_INSET = 72

interface NotificationInboxRowSkeletonProps {
  actionWidth?: string
  isFirst: boolean
  isLast: boolean
  showAction?: boolean
}

const NotificationInboxRowSkeleton = ({
  actionWidth = '42%',
  isFirst,
  isLast,
  showAction,
}: NotificationInboxRowSkeletonProps) => {
  const styles = useStyleSheet(themedStyles)

  return (
    <View
      style={[
        styles.rowLoader,
        showAction ? styles.rowLoaderWithAction : null,
        isFirst ? styles.rowLoaderFirst : null,
        isLast ? styles.rowLoaderLast : null,
      ]}
    >
      <ContentLoader variant="card" width="100%" height="100%" style={styles.rowContentLoader}>
        <Circle cx="36" cy="36" r="20" />
        <Rect x="68" y="18" width="31%" height="10" rx="4" ry="4" />
        <Rect x="80%" y="18" width="14%" height="10" rx="4" ry="4" />
        <Rect x="68" y="42" width="76%" height="14" rx="4" ry="4" />
        <Rect x="68" y="64" width="62%" height="14" rx="4" ry="4" />
        {showAction && <Rect x="68" y="94" width={actionWidth} height="40" rx="20" ry="20" />}
      </ContentLoader>
      {!isLast && <View style={styles.rowDivider} />}
    </View>
  )
}

interface NotificationInboxSectionSkeletonProps {
  labelWidth: string
  rows: Array<{ key: string; showAction?: boolean; actionWidth?: string }>
}

const NotificationInboxSectionSkeleton = ({
  labelWidth,
  rows,
}: NotificationInboxSectionSkeletonProps) => {
  const styles = useStyleSheet(themedStyles)

  return (
    <View style={styles.section}>
      <ContentLoader width="100%" height={LABEL_HEIGHT} style={styles.sectionLabelLoader}>
        <Rect x="0" y="0" width={labelWidth} height="12" rx="4" ry="4" />
      </ContentLoader>
      {rows.map((row, index) => {
        const isFirst = index === 0
        const isLast = index === rows.length - 1

        return (
          <NotificationInboxRowSkeleton
            actionWidth={row.actionWidth}
            isFirst={isFirst}
            isLast={isLast}
            key={row.key}
            showAction={row.showAction}
          />
        )
      })}
    </View>
  )
}

export const NotificationInboxLoadingContent = () => {
  const styles = useStyleSheet(themedStyles)

  return (
    <ScrollView
      contentContainerStyle={styles.container}
      showsVerticalScrollIndicator={false}
      style={styles.scroll}
      testID="NotificationInbox/Loading"
    >
      <NotificationInboxSectionSkeleton
        labelWidth="14%"
        rows={[
          { key: 'today-1' },
          { key: 'today-2', showAction: true, actionWidth: '44%' },
          { key: 'today-3', showAction: true, actionWidth: '28%' },
        ]}
      />
      <NotificationInboxSectionSkeleton
        labelWidth="24%"
        rows={[{ key: 'yesterday-1', showAction: true, actionWidth: '40%' }]}
      />
      <NotificationInboxSectionSkeleton
        labelWidth="26%"
        rows={[{ key: 'last7-1' }, { key: 'last7-2' }]}
      />
    </ScrollView>
  )
}

export const NotificationInboxFooterLoader = () => {
  const styles = useStyleSheet(themedStyles)

  return (
    <View style={styles.footer} testID="NotificationInbox/FooterLoading">
      <NotificationInboxRowSkeleton isFirst showAction={false} isLast={false} />
      <NotificationInboxRowSkeleton isFirst={false} showAction actionWidth="36%" isLast />
    </View>
  )
}

const themedStyles = StyleService.create({
  scroll: {
    flex: 1,
  },
  container: {
    padding: 16,
    paddingBottom: 24,
  },
  footer: {
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  rowLoader: {
    backgroundColor: 'theme.background',
    borderColor: 'theme.surface.base1',
    borderLeftWidth: 1,
    borderRightWidth: 1,
    height: ROW_HEIGHT,
    width: '100%',
    overflow: 'hidden',
  },
  rowContentLoader: {
    flex: 1,
  },
  rowDivider: {
    backgroundColor: 'theme.surface.base1',
    height: 1,
    marginLeft: DIVIDER_INSET,
  },
  rowLoaderFirst: {
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderTopWidth: 1,
  },
  rowLoaderLast: {
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomWidth: 1,
  },
  rowLoaderWithAction: {
    height: ROW_HEIGHT_WITH_ACTION,
  },
  section: {
    marginBottom: 20,
  },
  sectionLabelLoader: {
    marginTop: 2,
    marginBottom: 16,
    height: 12,
  },
})
